import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';
import { ActivatedRoute } from '@angular/router';
import { finalize, Observable, Subject, takeUntil, throwError } from 'rxjs';

import { FhcoEventContent, FhcoMessage, FhcoUserContext } from '../../integration/fhco-post-message/fhco-post-message.model';
import { FhcoPostMessageService } from '../../integration/fhco-post-message/fhco-post-message.service';
import { FormSubmissionResponse, FormSubmissionService } from '../../services/form-submission.service';
import { FormPageComponent } from '../../shared/form-page/form-page.component';
import { FORM_A_IDENTIFIER } from '../form-registry';
import { createFormularAForm, createPersonFormGroup, getPersonsFormArray, isFormularAPersonEmpty, mapFormularAFormToData } from './formular-a-form.factory';
import { FormularAData, FormularAPersonData, FormularASubmissionContext } from './formular-a.model';

@Component({
  selector: 'app-formular-a',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatStepperModule,
    FormPageComponent
  ],
  templateUrl: './formular-a.component.html',
  styleUrl: '../shared/banking-form.scss'
})
export class FormularAComponent implements OnInit, AfterViewInit, OnDestroy {

  readonly form = createFormularAForm(this.formBuilder);

  isSubmitting = false;
  isLoadingInitialData = false;
  private userContext?: FhcoUserContext;
  private tenant?: string;
  private readonly destroyed$ = new Subject<void>();
  private destroyed = false;
  private changes = 0;
  private content?: HTMLElement;
  private resizeObserver?: ResizeObserver;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formSubmissionService: FormSubmissionService,
    private readonly fhcoPostMessageService: FhcoPostMessageService,
    private readonly route: ActivatedRoute,
    private readonly element: ElementRef<HTMLElement>
  ) {
  }

  ngOnInit(): void {
    this.fhcoPostMessageService.messages$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((message) => this.handleFhcoMessage(message));

    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.changes++;
      this.sendState();
    });
    this.isLoadingInitialData = !!this.resolveCaseId() && !!this.resolveDocumentId();
    this.sendReady();
  }

  ngAfterViewInit(): void {
    this.content = this.element.nativeElement.querySelector<HTMLElement>('.form-page__content') ?? undefined;
    if (this.content) {
      this.resizeObserver = new ResizeObserver(() => this.sendState());
      this.resizeObserver.observe(this.content);
      this.sendState();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get contractPartnerGroup(): FormGroup {
    return this.form.get('contractPartner') as FormGroup;
  }

  get persons(): FormArray<FormGroup> {
    return getPersonsFormArray(this.form);
  }

  addPerson(): void {
    this.form.markAsDirty();
    this.persons.push(createPersonFormGroup(this.formBuilder));
  }

  removePerson(index: number): void {
    if (this.persons.length <= 1) {
      return;
    }
    this.form.markAsDirty();
    this.persons.removeAt(index);
  }

  discardEmptyAdditionalPersons(): void {
    for (let index = this.persons.length - 1; index >= 0; index--) {
      if (this.persons.length > 1 && isFormularAPersonEmpty(this.persons.at(index).getRawValue() as Partial<FormularAPersonData>)) {
        this.persons.removeAt(index);
      }
    }
  }

  private handleFhcoMessage(message: FhcoMessage): void {
    switch (message.type) {
      case 'user-context':
        this.userContext = message.content as FhcoUserContext;
        if (!this.tenant && message.tenant) {
          this.tenant = message.tenant;
          this.loadSavedDataFromBackend(message.tenant);
        }
        break;
      case 'prefill':
        this.prefillForm(message.content);
        break;
      case 'data-capturing':
        this.handleFhcoSubmit('FHCO_DATA_CAPTURING', 'data-capturing', true, message.content as FhcoEventContent);
        break;
      case 'save-progress': {
        const content = message.content as FhcoEventContent;
        this.handleFhcoSubmit('FHCO_SAVE_PROGRESS', 'save-progress', content.validate === true, content);
        break;
      }
      case 'render-preview':
        this.handleRenderPreview();
        break;
    }
  }

  private handleFhcoSubmit(source: string, responseType: 'data-capturing' | 'save-progress', validate: boolean, eventContent: FhcoEventContent): void {
    const submission = this.submitToBackend(source, validate, eventContent);
    const savedChanges = this.changes;
    submission.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response) => {
        if (savedChanges === this.changes) {
          this.form.markAsPristine();
        }
        this.fhcoPostMessageService.send(responseType, { state: 'COMPLETE', message: response });
        this.sendState();
      },
      error: (error: unknown) => this.fhcoPostMessageService.send(responseType, {
        state: 'NOT_COMPLETE',
        message: error instanceof Error ? error.message : 'Formular A could not be submitted.'
      })
    });
  }

  private handleRenderPreview(): void {
    if (this.isLoadingInitialData) {
      this.fhcoPostMessageService.send('render-preview', { state: 'NOT_COMPLETE', message: 'Form data is still loading.' });
      return;
    }
    this.discardEmptyAdditionalPersons();
    const data = mapFormularAFormToData(this.form);
    this.formSubmissionService.renderPreview<FormularAData>({
      formIdentifier: FORM_A_IDENTIFIER,
      data
    }).subscribe({
      next: (response) => this.fhcoPostMessageService.send('render-preview', {
        state: 'COMPLETE',
        message: response.content
      }),
      error: () => this.fhcoPostMessageService.send('render-preview', {
        state: 'NOT_COMPLETE',
        message: 'Formular A preview could not be rendered.'
      })
    });
  }

  private loadSavedDataFromBackend(tenant: string): void {
    const caseId = this.route.snapshot.queryParamMap.get('caseId');
    const documentId = this.route.snapshot.queryParamMap.get('documentId');
    if (!caseId || !documentId) {
      this.isLoadingInitialData = false;
      return;
    }

    this.isLoadingInitialData = true;
    this.formSubmissionService.getSavedData<Partial<FormularAData>>(caseId, documentId, tenant)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.isLoadingInitialData = false;
          this.sendState();
        })
      )
      .subscribe({
        next: (savedData) => {
          if (savedData) {
            this.prefillForm(savedData);
          }
        },
        error: () => undefined
      });
  }

  private sendReady(): void {
    if (!this.destroyed) {
      this.fhcoPostMessageService.send('form-action-ready', {});
      this.sendState();
    }
  }

  private sendState(): void {
    if (this.destroyed) {
      return;
    }
    let heightPx: number | undefined;
    if (this.content) {
      const style = getComputedStyle(this.content.parentElement!);
      heightPx = Math.ceil(this.content.getBoundingClientRect().height
        + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0));
    }
    this.fhcoPostMessageService.send('form-action-state', { dirty: this.form.dirty, heightPx });
  }

  private prefillForm(content: unknown): void {
    const data = this.extractFormData(content);
    if (!data) {
      return;
    }

    this.patchForm(data);
    this.form.markAsPristine();
    this.sendState();
  }

  private patchForm(data: Partial<FormularAData>): void {
    const patch: Record<string, unknown> = {};
    if (data.contractPartner) {
      patch['contractPartner'] = data.contractPartner;
    }
    this.ensurePersonControls(data.persons?.length ?? 1);
    if (data.persons?.length) {
      patch['persons'] = data.persons;
    }
    this.form.patchValue(patch);
  }

  private extractFormData(content: unknown): Partial<FormularAData> | null {
    if (!this.isRecord(content)) {
      return null;
    }

    const candidate = this.unwrapFormData(content);
    if (!this.isRecord(candidate)) {
      return null;
    }

    if (!('contractPartner' in candidate) && !('persons' in candidate)) {
      return null;
    }

    return candidate as Partial<FormularAData>;
  }

  private unwrapFormData(content: Record<string, unknown>): unknown {
    return content['data']
      ?? content['savedData']
      ?? content['formData']
      ?? content['formularAData']
      ?? content;
  }

  private ensurePersonControls(requiredLength: number): void {
    while (this.persons.length < requiredLength) {
      this.persons.push(createPersonFormGroup(this.formBuilder));
    }
    while (this.persons.length > Math.max(requiredLength, 1)) {
      this.persons.removeAt(this.persons.length - 1);
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private submitToBackend(source: string, validate: boolean, eventContent?: FhcoEventContent): Observable<FormSubmissionResponse> {
    if (this.isLoadingInitialData) {
      return throwError(() => new Error('Form data is still loading.'));
    }
    this.discardEmptyAdditionalPersons();

    if (validate && this.form.invalid) {
      this.form.markAllAsTouched();
      return throwError(() => new Error('Formular A is incomplete.'));
    }

    this.isSubmitting = true;
    const data = mapFormularAFormToData(this.form);
    return this.formSubmissionService.submit<FormularAData, FormularASubmissionContext>({
      formIdentifier: FORM_A_IDENTIFIER,
      caseId: this.resolveCaseId(eventContent),
      documentId: this.resolveDocumentId(eventContent),
      source,
      data,
      context: {
        embedded: this.fhcoPostMessageService.isEmbedded(),
        userContext: this.userContext,
        tenant: this.tenant
      }
    }).pipe(finalize(() => {
      this.isSubmitting = false;
    }));
  }

  private resolveCaseId(eventContent?: FhcoEventContent): string | null {
    return eventContent?.caseId ?? this.route.snapshot.queryParamMap.get('caseId');
  }

  private resolveDocumentId(eventContent?: FhcoEventContent): string | null {
    return eventContent?.documentId ?? this.route.snapshot.queryParamMap.get('documentId');
  }
}
